/**
 * Everything that needs the AudioContext, built on the first gesture: the buses, the synth's
 * voices with their cap, the effects, the ambience and the synthesised music, and the one timer
 * that keeps the music fed.
 */

import type { SimEvent } from '../sim/types';
import { AmbienceSound } from './ambience';
import { Bus } from './bus';
import { TICK_MS } from './music/clock';
import { MusicPlayer } from './music/player';
import { compiledSong } from './music/songs';
import { mulberry32, type Rand } from './rand';
import { collapse, DUCKS, loudness, PRIORITY, Retrigger, TOP } from './scan';
import { SFX, UI_SFX } from './sfx';
import { type Playing, Synth } from './synth';
import type { Ambience, MusicTrack, UiSound, Volumes } from './types';
import type { VoiceSpec } from './voice';
import { admit, expire, type Slot } from './voicePool';

/** The variation stream's seed: any constant will do, so a session's sounds vary the same way each run. */
const SEED = 0x57a2c1;

type Held = Slot & {
  readonly voice: Playing;
  /** A menu blip: `hush` leaves these alone (the pause menu's own click must not be cut). */
  readonly ui: boolean;
};

export class Engine {
  private readonly bus: Bus;
  private readonly synth: Synth;
  private readonly rand: Rand = mulberry32(SEED);
  private readonly player: MusicPlayer;
  private readonly amb: AmbienceSound;
  private readonly gate = new Retrigger();
  private held: Held[] = [];

  constructor(Ctor: typeof AudioContext, volumes: Volumes) {
    this.bus = new Bus(Ctor, volumes);
    this.synth = new Synth(this.bus.ctx, this.bus.noise, this.bus.noiseHz);
    this.player = new MusicPlayer(this.bus, this.synth);
    this.amb = new AmbienceSound(this.bus, this.synth);
    // One timer for the page's life: the audio object lives as long as the game does.
    setInterval(() => this.player.tick(), TICK_MS);
  }

  resume(): void {
    this.bus.resume();
  }

  setVolumes(v: Volumes): void {
    this.bus.setVolumes(v);
  }

  events(events: readonly SimEvent[]): void {
    if (events.length === 0 || !this.bus.live) return;
    const now = this.bus.now;
    let duck = false;
    for (const hit of collapse(events)) {
      if (!this.gate.allow(hit.sound, now)) continue;
      const voices = SFX[hit.sound](this.rand, hit.n);
      this.fire(voices, PRIORITY[hit.sound], loudness(hit.count), hit.pan, false);
      if (DUCKS.has(hit.sound)) duck = true;
    }
    if (duck) this.bus.duck();
  }

  ambience(a: Ambience): void {
    this.amb.set(a);
  }

  ui(s: UiSound): void {
    if (this.bus.live) this.fire(UI_SFX[s](this.rand, 0), TOP, 1, 0, true);
  }

  /** The continuous sounds stop and everything still ringing fades at once; music plays on. */
  hush(): void {
    this.amb.set({ beam: false, lance: false });
    const now = this.bus.now;
    for (const h of this.held) if (!h.ui) h.voice.cut(now);
    this.held = this.held.filter((h) => h.ui);
  }

  /** The synthesised track, from the top (or carrying on, if it is already the one playing). */
  playSong(track: MusicTrack): void {
    this.player.play(track, compiledSong(track));
  }

  stopSong(quick = false): void {
    this.player.stop(quick);
  }

  /** Voices through the cap: each is admitted, steals older or lesser ones, or is dropped. */
  private fire(voices: readonly VoiceSpec[], priority: number, gain: number, pan: number, ui: boolean): void {
    const now = this.bus.now;
    this.held = expire(this.held, now);
    for (const v of voices) {
      const start = now + v.at;
      const slot: Slot = { start, end: start + Math.max(0.001, v.gate) + v.env.r, priority };
      if (!this.makeRoom(slot, now)) continue;
      const voice = this.synth.play(v, now, ui ? this.bus.ui : this.bus.sfx, gain, pan);
      this.held.push({ ...slot, voice, ui });
    }
  }

  /**
   * Steal until `slot` fits; false if something more important is in its way, and then nothing
   * is stolen for it (the stealing is worked out first and only done once it is known to succeed).
   */
  private makeRoom(slot: Slot, now: number): boolean {
    const rest = [...this.held];
    const victims: Held[] = [];
    for (;;) {
      const verdict = admit(rest, slot);
      if (verdict.kind === 'drop') return false;
      if (verdict.kind === 'room') break;
      victims.push(...rest.splice(verdict.index, 1));
    }
    // A victim sounds until the newcomer needs its place, and holds its own place until then.
    const at = Math.max(now, slot.start);
    for (const v of victims) {
      v.voice.cut(at);
      if (at > v.start) rest.push({ ...v, end: at });
    }
    this.held = rest;
    return true;
  }
}
